// i18next bootstrap for the customer portal.
//
// Initialized as a SIDE EFFECT of importing this module — `main.tsx` imports it
// before rendering so `t()` is ready on first paint (mirrors Nexus's
// `src/i18n/`). react-i18next reads this singleton; no <I18nextProvider> needed.
//
// Locked conventions (cross-repo brief §11.1):
//   • en is the source; es is the only translation. fallbackLng='en' — a missing
//     es key renders its en value, never a raw key.
//   • Catalogs are bundled JSON shipped IN the artifact (statically imported
//     below) — no runtime CDN / Google-Translate call, no VITE_*-baked copy, so
//     the same image promotes stage → prod unchanged.
//   • `common` is the only namespace for now and loads eagerly. Per-area
//     namespaces (quote, pricing, checkout, status, vocabulary) join in Phase 1
//     as each module is extracted.
//   • Semantic hierarchical keys; interpolation over concatenation; i18next
//     plural keys. escapeValue:false (React already escapes).

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { i18nFormat } from './formatters.js';
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  normalizeLocale,
} from './resolveLocale.js';

import enCommon from './locales/en/common.json';
import esCommon from './locales/es/common.json';
import enQuote from './locales/en/quote.json';
import esQuote from './locales/es/quote.json';
import enPricing from './locales/en/pricing.json';
import esPricing from './locales/es/pricing.json';
import enCheckout from './locales/en/checkout.json';
import esCheckout from './locales/es/checkout.json';
import enStatus from './locales/en/status.json';
import esStatus from './locales/es/status.json';
import enAuth from './locales/en/auth.json';
import esAuth from './locales/es/auth.json';

// One resources map. Adding a language = a new `locales/<code>/` set + an entry
// here and in SUPPORTED_LOCALES — no other change (no hardcoded 2-language
// assumption anywhere).
export const resources = {
  en: {
    common: enCommon,
    quote: enQuote,
    pricing: enPricing,
    checkout: enCheckout,
    status: enStatus,
    auth: enAuth,
  },
  es: {
    common: esCommon,
    quote: esQuote,
    pricing: esPricing,
    checkout: esCheckout,
    status: esStatus,
    auth: esAuth,
  },
} as const;

export const NAMESPACES = ['common', 'quote', 'pricing', 'checkout', 'status', 'auth'] as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: DEFAULT_LOCALE,
    // Reduce region variants to the base language we translate (es-MX → es).
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    // The explicit portal chain (cross-repo brief §11.2): the customer's picker
    // choice (localStorage) → browser language → 'en'. No per-user ERP fetch.
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
      // Keep the detector's output on the supported base-language set so it
      // agrees with resolveLocale()/normalizeLocale exactly.
      convertDetectedLanguage: (lng) => normalizeLocale(lng) ?? DEFAULT_LOCALE,
    },
    interpolation: {
      escapeValue: false, // React escapes on render.
      format: i18nFormat, // enables {{amount, currency}} / {{value, number}} / {{when, date}}
    },
    returnNull: false,
  });

export default i18n;
